<template>
    <div class="columns">
        <div class="column is-6">
            <div class="field">
                <label class="label">{{ translate("page.admin_edit_event.thumbnail_image") }}</label>
                <div class="image-upload-area image-upload-area--thumbnail" @click="thumbnailFileInput?.click()">
                    <img
                        v-if="thumbnailImage"
                        :src="thumbnailImage"
                        class="image-preview"
                        :alt="translate('page.admin_edit_event.thumbnail_image')"
                        @error="imgUrlAlt"
                    />
                    <div v-else class="image-placeholder">
                        <i class="fas fa-image fa-2x mb-2"></i>
                        <p class="image-upload-title">{{ translate("wizard.step4.logo_click_to_upload") }}</p>
                        <p class="help">{{ translate("wizard.step4.logo_size_hint") }}</p>
                        <p class="help">{{ translate("page.admin_edit_event.thumbnail_max_file_size_hint") }}</p>
                    </div>
                </div>
                <input
                    ref="thumbnailFileInput"
                    type="file"
                    class="is-hidden"
                    accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                    @change="onThumbnailFileChanged"
                />
            </div>
            <div class="field">
                <button v-if="thumbnailImage" class="button is-small" @click="removeImage('thumb')">
                    <span class="icon"><i class="fas fa-trash-alt"></i></span>
                    <span>{{ translate("common.delete") }}</span>
                </button>
            </div>
            <p class="help has-text-danger" v-if="invalidThumbnailSize">
                {{ translate("page.admin_edit_event.invalid_image_size") }}
            </p>
            <p class="help has-text-danger" v-if="invalidThumbnailFormat">
                {{ translate("page.admin_edit_event.invalid_image_type") }}
            </p>
            <LoadingSpinner2 v-if="thumbnailInProgress" />
        </div>
        <div class="column is-6">
            <div class="field">
                <label class="label">{{ translate("page.admin_edit_event.background_image") }}</label>
                <div class="image-upload-area image-upload-area--wide" @click="backgroundFileInput?.click()">
                    <img
                        v-if="backgroundImage"
                        :src="backgroundImage"
                        class="image-preview"
                        :alt="translate('page.admin_edit_event.background_image')"
                        @error="imgUrlAlt"
                    />
                    <div v-else class="image-placeholder">
                        <i class="fas fa-image fa-2x mb-2"></i>
                        <p class="image-upload-title">{{ translate("page.admin_edit_event.click_to_upload_background") }}</p>
                        <p class="help">{{ translate("page.admin_edit_event.background_image_guidance") }}</p>
                    </div>
                </div>
                <input
                    ref="backgroundFileInput"
                    type="file"
                    class="is-hidden"
                    accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                    @change="onBackgroundFileChanged"
                />
            </div>
            <div class="field">
                <button v-if="backgroundImage" class="button is-small" @click="removeImage('bkg')">
                    <span class="icon"><i class="fas fa-trash-alt"></i></span>
                    <span>{{ translate("common.delete") }}</span>
                </button>

                <p class="help has-text-danger" v-if="invalidBackgroundSize">
                    {{ translate("page.admin_edit_event.invalid_image_size") }}
                </p>
                <p class="help has-text-danger" v-if="invalidBackgroundFormat">
                    {{ translate("page.admin_edit_event.invalid_image_type") }}
                </p>
            </div>

            <LoadingSpinner2 v-if="backgroundInProgress" />
        </div>
    </div>
</template>

<script setup lang="ts">
    import { ref, computed } from "vue";
    import { translate } from "../../Common/translations";
    import { handleError } from "../../Common/errorHandler";
    import { successToast, errorToast } from "../../Common/toasts";
    import LoadingSpinner2 from "../../Common/LoadingSpinner2.vue";
    import { useEditEventStore } from "../editEventStore";
    import {
        hasValidFileSize,
        isValidImageFormat,
        validateImageDimensions,
        MAX_IMAGE_DIMENSION_PX,
        MAX_IMAGE_SIZE_MB,
    } from "../../Common/imageValidation";
    import eventsApi from "../../Api/events";

    const store = useEditEventStore();

    const emit = defineEmits<{
        imageChanged: [];
    }>();

    const thumbnailFileInput = ref<HTMLInputElement | null>(null);
    const backgroundFileInput = ref<HTMLInputElement | null>(null);

    const thumbnailInProgress = ref(false);
    const backgroundInProgress = ref(false);

    const invalidThumbnailSize = ref(false);
    const invalidBackgroundSize = ref(false);
    const invalidThumbnailFormat = ref(false);
    const invalidBackgroundFormat = ref(false);

    const thumbnailImage = computed({
        get: () => store.event.thumbnailImage,
        set: (value: string) => store.setEventProperty({ name: "thumbnailImage", value }),
    });

    const backgroundImage = computed({
        get: () => store.event.backgroundImage,
        set: (value: string) => store.setEventProperty({ name: "backgroundImage", value }),
    });

    const imgUrlAlt = (event: Event) => {
        const target = event.target as HTMLImageElement;
        target.src = "/img/no-image.png";
    };

    const onThumbnailFileChanged = (event: Event) => {
        const target = event.target as HTMLInputElement;
        const files = target.files;

        if (!files || files.length === 0) {
            return;
        }

        invalidThumbnailSize.value = false;
        invalidThumbnailFormat.value = false;

        const thumbnailFile = files[0];

        if (!hasValidFileSize(thumbnailFile, MAX_IMAGE_SIZE_MB)) {
            invalidThumbnailSize.value = true;
            target.value = "";
            return;
        }

        if (!isValidImageFormat(thumbnailFile)) {
            invalidThumbnailFormat.value = true;
            target.value = "";
            return;
        }

        validateImageDimensions(thumbnailFile, MAX_IMAGE_DIMENSION_PX).then(dimensionValidation => {
            if (!dimensionValidation.valid) {
                errorToast(dimensionValidation.error!);
                target.value = "";
                return;
            }

            thumbnailInProgress.value = true;

            eventsApi
                .uploadImage(store.event?.id ?? 0, "thumb", thumbnailFile)
                .then(() => {
                    emit("imageChanged");
                    successToast(translate("page.admin_edit_event.picture_persisted"), 2000);
                })
                .catch(handleError)
                .finally(() => {
                    thumbnailInProgress.value = false;
                    target.value = "";
                });
        });
    };

    const onBackgroundFileChanged = (event: Event) => {
        const target = event.target as HTMLInputElement;
        const files = target.files;

        if (!files || files.length === 0) {
            return;
        }

        invalidBackgroundSize.value = false;
        invalidBackgroundFormat.value = false;

        const backgroundFile = files[0];

        if (!hasValidFileSize(backgroundFile, MAX_IMAGE_SIZE_MB)) {
            invalidBackgroundSize.value = true;
            target.value = "";
            return;
        }

        if (!isValidImageFormat(backgroundFile)) {
            invalidBackgroundFormat.value = true;
            target.value = "";
            return;
        }

        validateImageDimensions(backgroundFile, MAX_IMAGE_DIMENSION_PX).then(dimensionValidation => {
            if (!dimensionValidation.valid) {
                errorToast(dimensionValidation.error!);
                target.value = "";
                return;
            }

            backgroundInProgress.value = true;

            eventsApi
                .uploadImage(store.event?.id ?? 0, "bkg", backgroundFile)
                .then(() => {
                    emit("imageChanged");
                    successToast(translate("page.admin_edit_event.picture_persisted"), 2000);
                })
                .catch(handleError)
                .finally(() => {
                    backgroundInProgress.value = false;
                    target.value = "";
                });
        });
    };

    type ImageType = "bkg" | "thumb";

    const removeImage = (imageType: ImageType) => {
        if (imageType === "bkg") {
            backgroundInProgress.value = true;
        }
        if (imageType === "thumb") {
            thumbnailInProgress.value = true;
        }

        eventsApi
            .getAdminEvent(store.event.id)
            .then(fullEvent => {
                if (imageType === "bkg") {
                    fullEvent.event.backgroundImage = "";
                }
                if (imageType === "thumb") {
                    fullEvent.event.thumbnailImage = "";
                }

                return eventsApi.updateEventData(store.event.id, fullEvent);
            })
            .catch(handleError)
            .finally(() => {
                if (imageType === "bkg") {
                    backgroundInProgress.value = false;
                }
                if (imageType === "thumb") {
                    thumbnailInProgress.value = false;
                }

                emit("imageChanged");
            });
    };
</script>

<style scoped>
    .smaller-size {
        max-width: 500px;
        margin: 0 auto;
    }
</style>
